import React, {FC, ReactNode} from 'react';

import {RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {extractInitials} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {buildNoNameNoHandlesCardDisplayName, getNameForCard} from '../../../helpers/models/cardHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {Avatar, AvatarProps} from './avatar';
import {AvatarBackground} from './avatarBackground';
import {AvatarImage} from './avatarImage';
import {AvatarInitials} from './avatarInitials';

/*
 * Props.
 */

interface CardAvatarProps extends AvatarProps {
  card: CardModel;
  children?: ReactNode;
  className?: string;
  renderInitials?: RendererOf<string>;
}

/*
 * Component.
 */

export const CardAvatar: FC<CardAvatarProps> = (props) => {
  const {card} = props;
  const {avatarUrl} = card;
  const localize = useLocalize();

  if (avatarUrl) {
    return renderCardImageAvatar(props, avatarUrl);
  }

  // Otherwise, render the default avatar.
  const initials = extractInitials(getNameForCard(card) ?? buildNoNameNoHandlesCardDisplayName(localize));
  return renderCardColorAvatar(props, initials);
};

function renderCardImageAvatar(props: CardAvatarProps, avatarUrl: string) {
  return (
    <Avatar {...props}>
      <AvatarImage imageUrl={avatarUrl} />
      {props.children}
    </Avatar>
  );
}

function renderCardColorAvatar(props: CardAvatarProps, initials: string) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Avatar {...props}>
          <AvatarBackground color={theme.greys.shade70} />
          {renderCardInitials(props, initials)}
          {props.children}
        </Avatar>
      )}
    </UserThemeConsumer>
  );
}

function renderCardInitials(props: CardAvatarProps, initials: string) {
  const {size, renderInitials} = props;
  if (renderInitials) {
    return renderInitials(initials);
  }
  return (
    <UserThemeConsumer>
      {(theme) => <AvatarInitials size={size} textColor={theme.greys.white} value={initials} />}
    </UserThemeConsumer>
  );
}
